import React, { useState } from "react";
import {
  ChevronRight,
  Star,
  Trophy,
  Target,
  Zap,
  Clock,
  TrendingUp,
  Mail,
  CheckCircle,
  ArrowRight,
} from "lucide-react";

const WealthMethodQuiz = () => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<Record<number, any>>({});
  const [showResults, setShowResults] = useState(false);
  const [showEmailCapture, setShowEmailCapture] = useState(false);
  const [email, setEmail] = useState("");
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [userProfile, setUserProfile] = useState<any>(null);

  const questions = [
    {
      id: 1,
      title: "What's your biggest motivation for starting an online business?",
      subtitle:
        "This reveals your core driving force - the foundation of sustainable success",
      options: [
        {
          value: "freedom",
          text: "Time and location freedom",
          points: { freedom: 3, passion: 1 },
        },
        {
          value: "money",
          text: "Financial independence",
          points: { scale: 3, optimize: 1 },
        },
        {
          value: "impact",
          text: "Making a meaningful impact",
          points: { passion: 3, authority: 1 },
        },
        {
          value: "growth",
          text: "Personal growth and challenge",
          points: { optimize: 2, sustain: 2 },
        },
      ],
    },
    {
      id: 2,
      title: "How do you prefer to share your knowledge?",
      subtitle:
        "Your natural communication style determines your best platform strategy",
      options: [
        {
          value: "writing",
          text: "Writing detailed guides and articles",
          points: { authority: 3, convert: 1 },
        },
        {
          value: "video",
          text: "Creating videos and tutorials",
          points: { authority: 2, scale: 2 },
        },
        {
          value: "speaking",
          text: "Live presentations and webinars",
          points: { convert: 3, scale: 1 },
        },
        {
          value: "community",
          text: "Building communities and discussions",
          points: { authority: 1, sustain: 3 },
        },
      ],
    },
    {
      id: 3,
      title: "What's your current biggest challenge with making money online?",
      subtitle:
        "Identifying your main obstacle helps prioritize which W.E.A.L.T.H. step to focus on first",
      options: [
        {
          value: "idea",
          text: "I don't know what to sell",
          points: { discover: 4 },
        },
        {
          value: "audience",
          text: "I can't build an audience",
          points: { authority: 4 },
        },
        {
          value: "sales",
          text: "I struggle to make sales",
          points: { convert: 4 },
        },
        {
          value: "scale",
          text: "I can't grow beyond small income",
          points: { scale: 4 },
        },
      ],
    },
    {
      id: 4,
      title: "How much time can you realistically dedicate daily?",
      subtitle:
        "Your available time determines the optimal strategy and timeline for success",
      options: [
        {
          value: "1hour",
          text: "1-2 hours (side hustle mode)",
          points: { convert: 2, optimize: 2 },
        },
        {
          value: "3hours",
          text: "3-4 hours (serious commitment)",
          points: { authority: 2, scale: 2 },
        },
        {
          value: "6hours",
          text: "6+ hours (full-time focus)",
          points: { scale: 3, sustain: 1 },
        },
        {
          value: "flexible",
          text: "Flexible schedule",
          points: { freedom: 2, optimize: 2 },
        },
      ],
    },
    {
      id: 5,
      title: "What's your experience with online marketing?",
      subtitle:
        "Your current skill level helps customize the perfect starting point for you",
      options: [
        {
          value: "beginner",
          text: "Complete beginner",
          points: { discover: 2, authority: 2 },
        },
        {
          value: "some",
          text: "Some experience, inconsistent results",
          points: { convert: 2, optimize: 2 },
        },
        {
          value: "intermediate",
          text: "Making some money, want to scale",
          points: { scale: 3, optimize: 1 },
        },
        {
          value: "advanced",
          text: "Experienced, seeking optimization",
          points: { optimize: 3, sustain: 1 },
        },
      ],
    },
    {
      id: 6,
      title: "What would success look like for you in 12 months?",
      subtitle:
        "Your vision shapes the entire strategy - dream big, plan smart",
      options: [
        {
          value: "replace",
          text: "Replace my current job income",
          points: { convert: 2, scale: 2 },
        },
        {
          value: "double",
          text: "Double my current income",
          points: { scale: 3, optimize: 1 },
        },
        {
          value: "sixfig",
          text: "Hit six figures annually",
          points: { scale: 2, sustain: 2 },
        },
        {
          value: "freedom",
          text: "True time and financial freedom",
          points: { sustain: 3, freedom: 1 },
        },
      ],
    },
  ] as const;

  const profiles: Record<string, any> = {
    discover: {
      title: "The Opportunity Seeker",
      subtitle: "You're at the perfect starting point for explosive growth",
      description:
        "You have the drive and ambition, but you need clarity on WHAT to sell and WHO to serve. The good news? This is exactly where every successful entrepreneur started.",
      keyInsight:
        "Your biggest breakthrough will come from the DISCOVER phase - finding your profitable sweet spot where passion meets market demand.",
      nextSteps: [
        "Complete the personal inventory audit to uncover hidden opportunities",
        "Learn the MVP validation process to test ideas quickly",
        "Master the art of identifying customer pain points",
      ],
      wealthSteps: ["DISCOVER", "BUILD", "CONVERT"],
      icon: Target,
      color: "from-blue-500 to-cyan-500",
    },
    authority: {
      title: "The Content Creator",
      subtitle: "You're ready to build your expert platform",
      description:
        "You know what you want to share with the world, but you need the right strategy to build authority and attract your ideal audience consistently.",
      keyInsight:
        "Your path to success runs through the BUILD phase - creating content that positions you as the go-to expert in your niche.",
      nextSteps: [
        "Implement the authority-building content framework",
        "Create irresistible lead magnets that build your list",
        "Master platform-specific strategies for maximum reach",
      ],
      wealthSteps: ["BUILD", "CONVERT", "SCALE"],
      icon: Star,
      color: "from-purple-500 to-pink-500",
    },
    convert: {
      title: "The Sales Optimizer",
      subtitle: "You're ready to turn traffic into revenue",
      description:
        "You can attract people, but you struggle to convert them into paying customers. You need psychology-driven systems that sell automatically.",
      keyInsight:
        "Your breakthrough lies in the CONVERT phase - building automated systems that turn your audience into loyal, paying customers.",
      nextSteps: [
        "Create email sequences that nurture and sell automatically",
        "Build high-converting sales pages using proven formulas",
        "Master the digital customer journey",
      ],
      wealthSteps: ["CONVERT", "SCALE", "OPTIMIZE"],
      icon: Zap,
      color: "from-orange-500 to-red-500",
    },
    scale: {
      title: "The Growth Accelerator",
      subtitle: "You're ready to multiply your success",
      description:
        "You're making money, but you want to break through to the next level. You need systems and strategies that work without your constant involvement.",
      keyInsight:
        "Your next level awaits in the SCALE phase - building systems that multiply your reach and revenue exponentially.",
      nextSteps: [
        "Execute strategic launches that create market buzz",
        "Master paid traffic across multiple platforms",
        "Build systems and hire team members for growth",
      ],
      wealthSteps: ["SCALE", "OPTIMIZE", "SUSTAIN"],
      icon: TrendingUp,
      color: "from-green-500 to-emerald-500",
    },
    optimize: {
      title: "The Performance Maximizer",
      subtitle: "You're ready to fine-tune for maximum results",
      description:
        "You have a working business, but you know there's room for improvement. Small optimizations can lead to massive profit increases.",
      keyInsight:
        "Your competitive advantage comes from the OPTIMIZE phase - using data to maximize every aspect of your business performance.",
      nextSteps: [
        "Track the KPIs that actually matter",
        "Implement systematic A/B testing",
        "Optimize customer lifetime value through retention",
      ],
      wealthSteps: ["OPTIMIZE", "SUSTAIN", "SCALE"],
      icon: Trophy,
      color: "from-indigo-500 to-purple-500",
    },
    sustain: {
      title: "The Wealth Builder",
      subtitle: "You're ready for long-term success and freedom",
      description:
        "You're thinking beyond just making money - you want to build lasting wealth and create a business that serves your life, not the other way around.",
      keyInsight:
        "Your ultimate goal aligns with the SUSTAIN phase - building systems for true financial freedom and lasting impact.",
      nextSteps: [
        "Master entrepreneur-specific productivity systems",
        "Implement proper wealth-building strategies",
        "Create systems that thrive beyond your daily involvement",
      ],
      wealthSteps: ["SUSTAIN", "OPTIMIZE", "SCALE"],
      icon: Clock,
      color: "from-teal-500 to-green-500",
    },
  };

  const calculateProfile = () => {
    const scores: Record<string, number> = {
      discover: 0,
      authority: 0,
      convert: 0,
      scale: 0,
      optimize: 0,
      sustain: 0,
      passion: 0,
      freedom: 0,
    };

    Object.values(answers).forEach((answer: any) => {
      if (answer.points) {
        Object.entries(answer.points).forEach(([key, value]) => {
          scores[key] += value as number;
        });
      }
    });

    const maxScore = Math.max(
      scores.discover,
      scores.authority,
      scores.convert,
      scores.scale,
      scores.optimize,
      scores.sustain
    );

    const primaryProfile = Object.entries(scores).find(
      ([key, value]) =>
        [
          "discover",
          "authority",
          "convert",
          "scale",
          "optimize",
          "sustain",
        ].includes(key) && value === maxScore
    );

    return profiles[primaryProfile ? primaryProfile[0] : "discover"];
  };

  const handleAnswerSelect = (answer: any) => {
    setAnswers({ ...answers, [currentQuestion]: answer });

    if (currentQuestion < questions.length - 1) {
      setTimeout(() => {
        setCurrentQuestion(currentQuestion + 1);
      }, 300);
    } else {
      setTimeout(() => {
        const profile = calculateProfile();
        setUserProfile(profile);
        setShowResults(true);
      }, 300);
    }
  };

  const handleEmailSubmit = () => {
    if (email.includes("@") && email.includes(".")) {
      setEmailSubmitted(true);
      setShowEmailCapture(false);
    }
  };

  const progress = ((currentQuestion + 1) / questions.length) * 100;

  if (emailSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <div className="max-w-2xl mx-auto text-center">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-12 border border-white/20">
            <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-white mb-4">
              Check Your Email!
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Your complete W.E.A.L.T.H. Method profile and personalized action
              plan has been sent to <strong>{email}</strong>
            </p>
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-6 text-left">
              <h3 className="text-xl font-semibold text-white mb-3">
                What's Coming Next:
              </h3>
              <ul className="text-gray-100 space-y-2">
                <li>• Your detailed entrepreneurial profile analysis</li>
                <li>• Step-by-step action plan for the next 90 days</li>
                <li>• Exclusive bonus strategies not covered in this quiz</li>
                <li>• Access to our private community of entrepreneurs</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (showEmailCapture) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20">
            <div className="text-center mb-8">
              <Mail className="w-16 h-16 text-blue-400 mx-auto mb-4" />
              <h2 className="text-3xl font-bold text-white mb-4">
                Get Your Complete W.E.A.L.T.H. Method Profile
              </h2>
              <p className="text-xl text-gray-300 mb-6">
                Enter your email to receive your detailed results plus exclusive
                bonuses worth $297
              </p>
            </div>

            <div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-2xl p-6 mb-8">
              <h3 className="text-xl font-semibold text-white mb-4">
                You'll Also Get:
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-200">
                <div className="flex items-start space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                  <span>90-Day Quick Start Guide</span>
                </div>
                <div className="flex items-start space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                  <span>Profitable Niche Finder Tool</span>
                </div>
                <div className="flex items-start space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                  <span>Authority Content Templates</span>
                </div>
                <div className="flex items-start space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                  <span>Conversion Optimization Checklist</span>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                className="w-full px-6 py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 text-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                onClick={handleEmailSubmit}
                disabled={!email}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-xl text-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span>Send Me My Complete Profile</span>
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>

            <p className="text-center text-gray-400 text-sm mt-4">
              No spam, ever. Unsubscribe anytime.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (showResults && userProfile) {
    const IconComponent = userProfile.icon;

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-white mb-4">
              Your W.E.A.L.T.H. Method Profile
            </h1>
            <p className="text-xl text-gray-300">
              Based on your answers, here's your personalized roadmap to success
            </p>
          </div>

          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20 mb-8">
            <div className="text-center mb-8">
              <div
                className={`w-24 h-24 rounded-full bg-gradient-to-r ${userProfile.color} flex items-center justify-center mx-auto mb-6`}
              >
                <IconComponent className="w-12 h-12 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-white mb-2">
                {userProfile.title}
              </h2>
              <p className="text-xl text-blue-300 mb-4">
                {userProfile.subtitle}
              </p>
              <p className="text-lg text-gray-300 max-w-2xl mx-auto">
                {userProfile.description}
              </p>
            </div>

            <div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-2xl p-6 mb-8">
              <h3 className="text-xl font-semibold text-white mb-3 flex items-center">
                <Zap className="w-5 h-5 mr-2 text-yellow-400" />
                Your Key Insight
              </h3>
              <p className="text-gray-200 text-lg">{userProfile.keyInsight}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              <div>
                <h3 className="text-xl font-semibold text-white mb-4">
                  Your Priority W.E.A.L.T.H. Steps
                </h3>
                <div className="space-y-3">
                  {userProfile.wealthSteps.map((step: any, index: any) => (
                    <div key={step} className="flex items-center space-x-3">
                      <div className="bg-gradient-to-r from-blue-500 to-purple-500 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold">
                        {index + 1}
                      </div>
                      <span className="text-gray-300 font-medium">{step}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-white mb-4">
                  Your Next Steps
                </h3>
                <ul className="space-y-3">
                  {userProfile.nextSteps.map((step: any, index: any) => (
                    <li key={index} className="flex items-start space-x-2">
                      <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                      <span className="text-gray-300">{step}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="text-center">
              <p className="text-lg text-gray-300 mb-6">
                This is just the beginning. Get your complete profile with
                detailed strategies, timelines, and bonus resources.
              </p>
              <button
                onClick={() => setShowEmailCapture(true)}
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-xl text-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 flex items-center space-x-2 mx-auto"
              >
                <span>Get My Complete W.E.A.L.T.H. Method Guide</span>
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-4">
            Discover Your Path to Digital Freedom
          </h1>
          <p className="text-xl text-gray-300 mb-6">
            Take the W.E.A.L.T.H. Method Assessment and uncover your
            personalized roadmap to six-figure success
          </p>

          {/* Progress Bar */}
          <div className="bg-white/10 rounded-full h-3 mb-4">
            <div
              className="bg-gradient-to-r from-blue-500 to-purple-500 h-3 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-gray-400">
            Question {currentQuestion + 1} of {questions.length}
          </p>
        </div>

        {/* Question Card */}
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20">
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">
              {questions[currentQuestion].title}
            </h2>
            <p className="text-lg text-gray-300">
              {questions[currentQuestion].subtitle}
            </p>
          </div>

          <div className="grid gap-4">
            {questions[currentQuestion].options.map((option, index) => (
              <button
                key={index}
                onClick={() => handleAnswerSelect(option)}
                className="text-left p-6 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 rounded-2xl transition-all duration-300 group"
              >
                <div className="flex items-center justify-between">
                  <span className="text-lg text-white group-hover:text-blue-300 transition-colors duration-300">
                    {option.text}
                  </span>
                  <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-blue-300 transition-colors duration-300" />
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-8">
          <p className="text-gray-400">
            Based on thousands of successful entrepreneurs who've used the
            W.E.A.L.T.H. Method
          </p>
        </div>
      </div>
    </div>
  );
};

export default WealthMethodQuiz;
